import { createApiClient, type Middleware } from '@ccp/api-client';
import { getAccessToken } from './auth-tokens.js';
import { ApiError, toApiError } from './errors.js';

/** The API's origin. Same-origin in a deployment, behind Caddy; overridable for development. */
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/';

/** What this client is, so the API can enforce a version policy (api-and-contracts: versioning). */
const clientHeader = `workspace/${import.meta.env.VITE_APP_VERSION ?? '0.0.0'}`;

const authentication: Middleware = {
  onRequest({ request }) {
    const token = getAccessToken();
    if (token !== undefined) {
      request.headers.set('authorization', `Bearer ${token}`);
    }
    request.headers.set('x-client', clientHeader);
    return request;
  },
};

const errors: Middleware = {
  async onResponse({ response }) {
    if (response.ok) {
      return response;
    }
    // Read the envelope once here so every caller gets an ApiError, not a raw Response.
    const body: unknown = await response
      .clone()
      .json()
      .catch(() => undefined);
    throw toApiError(response.status, body);
  },
};

/**
 * The application's one API client. Features define their queries and mutations against it in
 * their own `api.ts`; nothing else constructs a client.
 */
export const api = createApiClient({ baseUrl });
api.use(authentication);
api.use(errors);

/**
 * The body of a call that is supposed to have one.
 *
 * The generated types make `data` optional because a failure has none, but the error middleware
 * above has already thrown by then — so this is the one place that says so, rather than every
 * caller carrying a check it cannot reach.
 */
export function expectBody<T>(result: { data?: T | undefined }): T {
  if (result.data === undefined) {
    throw new ApiError(500, { message: 'the API answered without a body' });
  }
  return result.data;
}
